import React from 'react';
import { Button, Empty, Table, Tooltip } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { FEATURE_FLAG_STATUS, FeatureFlagItem } from '../../../lib/api/featureFlag';
import { formatLogDate } from '../../pgProviderRoute/utils/formatLogDate';
import { STATUS_LABEL, defaultHint, onOff, splitDescription } from '../utils/featureFlagLabels';
import { OnOffPill } from './styles';

type FeatureFlagTableProps = {
  flags: FeatureFlagItem[];
  canWrite: boolean;
  isLoading: boolean;
  onEdit: (flag: FeatureFlagItem) => void;
};

export const FeatureFlagTable = (props: FeatureFlagTableProps): React.JSX.Element => {
  const { flags, canWrite, isLoading, onEdit } = props;

  const columns: ColumnsType<FeatureFlagItem> = [
    {
      title: '플래그',
      dataIndex: 'ticketKey',
      render: (_, flag) => {
        const { apps, text } = splitDescription(flag.description);
        return (
          <div className={flag.locked ? 'flag-locked' : undefined}>
            <div className="flag-ticket">
              {flag.ticketNumber ?? flag.ticketKey}
              {apps.map((app) => (
                <span className="flag-app" key={app}>
                  {app}
                </span>
              ))}
            </div>
            {text && <div className="flag-description">{text}</div>}
            {flag.memo && (
              <div className="flag-memo">
                <b>메모</b>
                {flag.memo}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '상태값',
      dataIndex: 'status',
      width: 110,
      render: (_, flag) =>
        flag.status && (
          <span className={`flag-status is-${flag.status}`}>
            {flag.status === FEATURE_FLAG_STATUS.IN_DEVELOPMENT && <LockOutlined aria-hidden />}
            {STATUS_LABEL[flag.status]}
          </span>
        ),
    },
    {
      title: '기본값',
      dataIndex: 'defaultEnabled',
      width: 100,
      render: (_, flag) => <span className={flag.locked ? 'flag-locked-text' : undefined}>{defaultHint(flag)}</span>,
    },
    {
      title: '덮어쓴 값',
      dataIndex: 'dbEnabled',
      width: 130,
      render: (_, flag) => {
        if (flag.status === FEATURE_FLAG_STATUS.IN_DEVELOPMENT)
          return <span className="flag-locked-text">덮어쓸 수 없음</span>;
        if (flag.dbEnabled === null) return <span className="settings-muted">기본값 따름</span>;
        return (
          <Tooltip
            title={
              flag.dbEnabled === flag.defaultEnabled
                ? '상태값이 바뀌어 지금은 기본값과 같습니다. 그래도 상태값이 다시 바뀌면 이 값이 남습니다.'
                : undefined
            }
          >
            <span className="flag-override">덮어씀 · {onOff(flag.dbEnabled)}</span>
          </Tooltip>
        );
      },
    },
    {
      title: '지금',
      dataIndex: 'enabled',
      width: 80,
      render: (enabled: boolean) => <OnOffPill $enabled={enabled}>{onOff(enabled)}</OnOffPill>,
    },
    {
      title: '마지막 변경',
      dataIndex: 'updateDate',
      width: 150,
      render: (_, flag) =>
        flag.updateDate ? (
          <>
            <div>{flag.adminName ?? (flag.adminId ? '관리자 #' + flag.adminId : '-')}</div>
            <div className="settings-muted">{formatLogDate(flag.updateDate)}</div>
          </>
        ) : (
          <span className="settings-muted">-</span>
        ),
    },
    {
      title: '',
      width: 96,
      render: (_, flag) =>
        canWrite &&
        (flag.locked ? (
          <Tooltip title="개발 중인 플래그는 QA 로 올리는 PR 이 배포돼야 바꿀 수 있습니다">
            <Button size="small" disabled>
              잠김
            </Button>
          </Tooltip>
        ) : (
          <Button size="small" onClick={() => onEdit(flag)}>
            바꾸기
          </Button>
        )),
    },
  ];

  return (
    <Table
      rowKey="ticketKey"
      size="middle"
      columns={columns}
      dataSource={flags}
      loading={isLoading}
      pagination={false}
      scroll={{ x: 980 }}
      rowClassName={(flag) => (flag.dbEnabled !== null && !flag.locked ? 'flag-row-overridden' : '')}
      locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="표시할 플래그가 없습니다." /> }}
    />
  );
};
